import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { cn } from '@reflex/ui/lib/utils';
import { getActiveOrgPreference, getApiUrl, saveReturnPath } from '@/lib/api';

const WorkEmailSchema = z.object({
  email: z.string().trim().email('Enter a valid work email.'),
});
type WorkEmail = z.infer<typeof WorkEmailSchema>;

// Muted text affordance shared by the trigger link and the back button.
// Mirrors the "New to Reflex? Create an account" switch in EmailAuthForm, plus
// a focus ring so keyboard users can see where they are.
const textLinkClassName =
  'rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const triggerLabel = <span className="font-medium underline">Sign in with company SSO</span>;

/**
 * The single company SSO entry on the sign-in screen: a secondary text link
 * under the provider buttons, so enterprise SSO does not compete visually
 * with the sign-in methods almost every visitor actually uses.
 *
 * An organization URL or saved preference is enough to start immediately.
 * A first-time visitor instead sees the same link as a button; clicking it
 * reveals the work-email step in place (no navigation). Only the normalized
 * domain is sent to the unauthenticated start route, which returns the same
 * generic failure for every unavailable company and therefore does not reveal
 * account or configuration state.
 */
export function SignInWithSso({ className }: { className?: string }) {
  const organization = getActiveOrgPreference();
  // Transient UI state (not bookmarkable), so useState rather than nuqs.
  const [step, setStep] = useState<'button' | 'email'>('button');
  const form = useForm<WorkEmail>({
    resolver: zodResolver(WorkEmailSchema),
    defaultValues: { email: '' },
  });

  // Move focus into the email field as soon as the step appears so a button
  // press flows straight into typing for keyboard and screen-reader users.
  useEffect(() => {
    if (step === 'email') form.setFocus('email');
  }, [step, form]);

  if (organization) {
    const href = getApiUrl(`/auth/oidc/start?org=${encodeURIComponent(organization)}`);
    return (
      <div className={cn('flex justify-center', className)}>
        <a
          href={href}
          onClick={saveReturnPath}
          className={textLinkClassName}
          data-testid="sign-in-with-sso"
        >
          {triggerLabel}
        </a>
      </div>
    );
  }

  const submit = form.handleSubmit(({ email }) => {
    saveReturnPath();
    const domain = email
      .slice(email.lastIndexOf('@') + 1)
      .toLowerCase()
      .replace(/\.$/, '');
    window.location.assign(getApiUrl(`/auth/oidc/start?domain=${encodeURIComponent(domain)}`));
  });
  const error = form.formState.errors.email?.message;

  function goBack() {
    setStep('button');
    // Clear a stale validation error so it is not waiting when the visitor
    // returns to this step later.
    form.clearErrors('email');
  }

  if (step === 'button') {
    return (
      <div className={cn('flex justify-center', className)}>
        <button
          type="button"
          onClick={() => setStep('email')}
          className={textLinkClassName}
          data-testid="sign-in-with-sso"
        >
          {triggerLabel}
        </button>
      </div>
    );
  }

  return (
    <form className={className} onSubmit={submit} data-testid="sign-in-with-sso">
      <div className="space-y-2 rounded-lg border p-3">
        {/* Matches the back affordance EmailAuthForm uses for the same
            "step revealed inline, needs a way out" shape. BackLinkButton
            wraps a react-router Link and pushes a history entry, which
            fits crumb navigation between routes, not a local step back
            within one component. */}
        <button
          type="button"
          onClick={goBack}
          data-testid="sso-work-email-back"
          className={cn(textLinkClassName, '-ml-1 flex w-fit items-center gap-1 px-1 py-0.5')}
        >
          <ArrowLeft className="icon-sm" />
          Back
        </button>
        <div className="space-y-1">
          <Label htmlFor="sso-work-email">Company SSO</Label>
          <p className="text-xs text-muted-foreground">
            Enter your work email to find your company&apos;s sign-in page.
          </p>
        </div>
        <Input
          id="sso-work-email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'sso-work-email-error' : undefined}
          {...form.register('email')}
        />
        {error ? (
          <p id="sso-work-email-error" role="alert" className="text-xs text-destructive">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="outline" className="w-full">
          Continue with SSO
        </Button>
      </div>
    </form>
  );
}
